import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchOriginal, type OriginalDetail as Original } from '../lib/api';
import { geneUrl } from '../lib/memegenes';
import { GeneCheckIcon } from '../components/GeneCheckIcon';

/**
 * Original Detail — Provny's editorial view of a verified original. The work is
 * the hero; a clean provenance summary states the real proof in Provny voice,
 * and the Verified-by-MemeGenes block links out to the Gene for the full
 * technical provenance. Provny links, it does not reproduce. See BRAND.md
 * ▸ Relationship to MemeGenes.
 */
export function OriginalDetail() {
  const { id } = useParams();
  const [state, setState] = useState<'loading' | 'not-found' | 'loaded'>('loading');
  const [original, setOriginal] = useState<Original | null>(null);

  useEffect(() => {
    let active = true;
    setState('loading');
    if (!id) {
      setState('not-found');
      return;
    }
    fetchOriginal(id).then((result) => {
      if (!active) return;
      if (!result) {
        setState('not-found');
      } else {
        setOriginal(result);
        setState('loaded');
      }
    });
    return () => {
      active = false;
    };
  }, [id]);

  if (state === 'loading') {
    return (
      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div className="aspect-[4/5] animate-pulse rounded-2xl border border-provny-border bg-provny-surface dark:border-provny-border-dark dark:bg-provny-surface-dark" />
          <div className="flex flex-col gap-6">
            <div className="h-10 w-2/3 animate-pulse rounded bg-provny-surface dark:bg-provny-surface-dark" />
            <div className="h-24 animate-pulse rounded-2xl bg-provny-surface dark:bg-provny-surface-dark" />
          </div>
        </div>
      </main>
    );
  }

  if (state === 'not-found' || !original) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-24 text-center">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Original not found</h1>
        <p className="mt-3 text-provny-muted">This original may have moved or never existed.</p>
        <Link to="/explore" className="mt-6 inline-block font-semibold text-provny-cyber-dim hover:underline dark:text-provny-cyber">
          ← Back to Explore
        </Link>
      </main>
    );
  }

  const live = original.postStatus === 'live';
  const isOriginal = (original.derivationType ?? 'original') === 'original';
  const handle = (original.creator.username ?? '').replace(/^@/, '') || null;
  const name = original.creator.displayName;
  const title = name ? `${name}, an original` : 'Verified original';

  return (
    <main className="mx-auto max-w-7xl px-6 py-8">
      <Link to="/explore" className="text-sm text-provny-muted transition-colors hover:text-provny-black dark:hover:text-provny-white">
        ← Explore
      </Link>

      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-2">
        {/* Content hero */}
        <div className="overflow-hidden rounded-2xl border border-provny-border bg-provny-surface dark:border-provny-border-dark dark:bg-provny-surface-dark">
          {original.imageUrl && (
            <img
              src={original.imageUrl}
              alt={name ? `Original by ${name}` : 'Original'}
              className={`w-full object-cover ${live ? '' : 'opacity-70'}`}
            />
          )}
        </div>

        {/* Editorial column */}
        <div className="flex flex-col gap-6">
          <div>
            <span className="text-xs font-medium uppercase tracking-wider text-provny-cyber-dim dark:text-provny-cyber">
              {isOriginal ? 'Original' : 'Derivative'}
            </span>
            <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{title}</h1>

            <div className="mt-3 flex items-center gap-3">
              {original.creator.profileImageUrl && (
                <img
                  src={original.creator.profileImageUrl}
                  alt={name ?? 'Creator'}
                  className="h-9 w-9 rounded-full object-cover"
                />
              )}
              <div className="leading-tight">
                {name && <p className="font-semibold">{name}</p>}
                {handle && (
                  <Link
                    to={`/@${handle}`}
                    className="text-sm text-provny-muted transition-colors hover:text-provny-black dark:hover:text-provny-white"
                  >
                    @{handle}
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Provenance summary — the editorial hero of this page */}
          <div className="rounded-2xl border border-provny-border bg-provny-surface p-5 dark:border-provny-border-dark dark:bg-provny-surface-dark">
            <p className="text-lg leading-relaxed text-provny-black/80 dark:text-provny-white/80">
              {provenanceLine(original)}
            </p>
            {original.createdAt != null && (
              <p className="mt-2 text-sm text-provny-muted">First seen {formatDate(original.createdAt)}.</p>
            )}
            {original.childrenCount > 0 && (
              <p className="mt-1 text-sm text-provny-muted">
                {original.childrenCount} derivative{original.childrenCount === 1 ? '' : 's'} traced.
              </p>
            )}
          </div>

          {original.socialPostUrl && (
            <a
              href={original.socialPostUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-provny-cyber-dim transition-colors hover:underline dark:text-provny-cyber"
            >
              View the original post ↗
            </a>
          )}

          {/* Verified by MemeGenes — present, not invisible */}
          <a
            href={geneUrl(original.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-4 rounded-2xl border border-provny-border p-5 transition-colors hover:border-provny-gold dark:border-provny-border-dark"
          >
            <GeneCheckIcon size={28} />
            <div className="flex-1">
              <p className="font-semibold">Verified by MemeGenes</p>
              <p className="text-sm text-provny-muted">
                Invisible proof of origin. View this original's Gene on MemeGenes.
              </p>
            </div>
            <span className="font-mono text-sm text-provny-muted transition-colors group-hover:text-provny-gold">
              {original.id} →
            </span>
          </a>
        </div>
      </div>
    </main>
  );
}

/** Confident, jargon-free statement of how widely the original has been seen. */
function provenanceLine(o: Original): string {
  const seen = o.sightingsCount === 1 ? 'Seen once' : `Seen ${o.sightingsCount} times`;
  if (o.platformCount <= 0) return `${seen}.`;
  return `${seen} across ${o.platformCount} platform${o.platformCount === 1 ? '' : 's'}.`;
}

/** Unix seconds → readable date, e.g. "June 20, 2026". */
function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
